import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  TextField,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Collapse,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Person,
  Business,
  PhoneAndroid,
  EmailOutlined,
} from "@mui/icons-material";
import { MuiTelInput } from "mui-tel-input";
import { useNavigate } from "react-router";
import SmsDogrulamaSaveAPI, {
  SmsDogrulamaSaveRequest,
} from "../../../apis/OB/SaveApi/SmsDogrulamaSaveAPI";
import SmsKoduKontrolAPI, {
  SmsDogrulamaKontrolRequest,
} from "../../../apis/OB/SaveApi/SmsKoduKontrolAPI";
import EmailAPI, { EmailRequest } from "../../../apis/EmailAPI";
import Recaptcha from "../../Recaptcha";
import { toast } from "react-toastify";

type UyelikTipi = "bireysel" | "kurumsal" | null;
type DogrulamaYontemi = "sms" | "eposta" | null;

const UyelikDogrulamaPopup = (props: any) => {
  // Adımlar: 1 = Kimlik Doğrulama, 2 = Doğrulama Yöntemi, 3 = Doğrulama Kodu
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

  // Adım 1
  const [uyelikTipi, setUyelikTipi] = useState<UyelikTipi>(null);
  const [tcKimlikNo, setTcKimlikNo] = useState("");
  const [vergiNo, setVergiNo] = useState("");

  // Adım 2
  const [dogrulamaYontemi, setDogrulamaYontemi] =
    useState<DogrulamaYontemi>(null);
  const [cepTelefonu, setCepTelefonu] = useState("");
  const [ePosta, setEPosta] = useState("");

  // Adım 3
  const [dogrulamaKodu, setDogrulamaKodu] = useState("");
  const [refNo, setRefNo] = useState<string>("");
  const [seconds, setSeconds] = useState(300);
  const [isActive, setIsActive] = useState(false);

  // Sicil seçimi
  const [sicilListesi, setSicilListesi] = useState<any[]>([]);
  const [secilenSicilNo, setSecilenSicilNo] = useState<number | null>(null);

  // Ortak
  const [isError, setIsError] = useState(false);
  const [errorContent, setErrorContent] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [recaptchaPassed, setRecaptchaPassed] = useState(false);

  const navigate = useNavigate();
  const recaptchaTokenRef: any = useRef();

  // Sayaç
  useEffect(() => {
    let interval: any = null;
    if (isActive && seconds > 0) {
      interval = setInterval(() => {
        setSeconds((s) => s - 1);
      }, 1000);
    } else if (seconds === 0) {
      setIsActive(false);
    }
    return () => clearInterval(interval);
  }, [isActive, seconds]);

  const showError = (message: string, duration = 4000) => {
    setErrorContent(message);
    setIsError(true);
    setTimeout(() => {
      setIsError(false);
      setErrorContent("");
    }, duration);
  };

  const handleRecaptchaChange = (value: boolean) => {
    setRecaptchaPassed(value);
  };

  // Telefon maskeleme
  function maskPhoneNumber(phoneNumber: string): string {
    const cleanedNumber = phoneNumber.replace(/\s+/g, "");
    if (cleanedNumber.length === 13 && cleanedNumber.startsWith("+90")) {
      const countryCode = cleanedNumber.substring(0, 3);
      const firstTwoDigits = cleanedNumber.substring(3, 5);
      const lastTwoDigits = cleanedNumber.substring(
        cleanedNumber.length - 2
      );
      return `${countryCode} ${firstTwoDigits}* *** ** ${lastTwoDigits}`;
    }
    return phoneNumber;
  }

  // ─── ADIM 1: Format kontrolü ───
  const handleKimlikDogrulama = () => {
    if (uyelikTipi === "bireysel") {
      if (!tcKimlikNo || tcKimlikNo.length !== 11) {
        showError("T.C. Kimlik Numarası 11 haneli olmalıdır.");
        return;
      }
    } else if (uyelikTipi === "kurumsal") {
      if (!vergiNo || vergiNo.length < 10) {
        showError("Vergi Numarası en az 10 haneli olmalıdır.");
        return;
      }
    }

    setIsError(false);
    setStep(2);
  };

  // ─── ADIM 2: Doğrulama kodu gönder ───
  const handleDogrulamaKoduGonder = async () => {
    const recaptchaToken =
      await recaptchaTokenRef.current?.handleSubmit();
    if (!recaptchaToken) {
      return;
    }

    if (dogrulamaYontemi === "sms") {
      const cleanPhone = cepTelefonu
        .replace(/^\+9/, "")
        .replace(/ /g, "");
      if (!cepTelefonu || cleanPhone.length < 10) {
        showError("Geçerli bir cep telefonu numarası giriniz.");
        return;
      }
      setIsLoading(true);
      try {
        const request = new SmsDogrulamaSaveRequest(
          cleanPhone,
          tcKimlikNo || undefined,
          uyelikTipi === "kurumsal" ? vergiNo : undefined,
          undefined,
          undefined,
          recaptchaToken,
          undefined,
          true
        );
        const res =
          await SmsDogrulamaSaveAPI.saveSmsDogrulamaSaveSave(request);
        if (res.data.sicilListesi && res.data.sicilListesi.length > 0) {
          setSicilListesi(res.data.sicilListesi);
          setSecilenSicilNo(null);
          setStep(4);
          setIsError(false);
        } else if (res.data.sonuc === 0) {
          setRefNo(request.referansNo!);
          setStep(3);
          setSeconds(300);
          setIsActive(true);
          setIsError(false);
        } else {
          toast.error(res.data.sonucAciklama);
          recaptchaTokenRef.current?.reset();
        }
      } catch (err) {
        recaptchaTokenRef.current?.reset();
      } finally {
        setIsLoading(false);
      }
    } else if (dogrulamaYontemi === "eposta") {
      if (!ePosta || !ePosta.includes("@")) {
        showError("Geçerli bir e-posta adresi giriniz.");
        return;
      }
      setIsLoading(true);
      try {
        const request = new EmailRequest(
          ePosta,
          tcKimlikNo || undefined,
          recaptchaToken,
          uyelikTipi === "kurumsal" ? vergiNo : undefined,
          false,
          true
        );
        const res = await EmailAPI.mailGonder(request);
        if (res.sonuc === 0) {
          setRefNo(res.referenceNumber || "");
          setStep(3);
          setSeconds(300);
          setIsActive(true);
          setIsError(false);
        } else {
          toast.error(res.sonucAciklama);
          recaptchaTokenRef.current?.reset();
        }
      } catch (err: any) {
        recaptchaTokenRef.current?.reset();
      } finally {
        setIsLoading(false);
      }
    }
  };

  // ─── ADIM 3: Doğrulama kodunu kontrol et ───
  const handleDogrula = async () => {
    if (dogrulamaKodu.length < 6) {
      showError("Doğrulama kodu 6 haneli olmalıdır.");
      return;
    }

    setIsLoading(true);
    try {
      const request = new SmsDogrulamaKontrolRequest(
        tcKimlikNo || "",
        refNo,
        dogrulamaKodu,
        uyelikTipi === "kurumsal" ? vergiNo : undefined,
        secilenSicilNo ?? undefined,
        undefined,
        dogrulamaYontemi === "eposta",
        true
      );
      const res =
        await SmsKoduKontrolAPI.saveSmsDogrulamaKontrolSave(request);
      if (res.data[0].sonuc === 0) {
        props.tcKimlikNoSet(tcKimlikNo);
        if (props.cepTelefonuSet != undefined)
          props.cepTelefonuSet(cepTelefonu);
        if (props.vergiNoSet != undefined)
          props.vergiNoSet(vergiNo);
        if (props.uyelikTipiSet != undefined)
          props.uyelikTipiSet(uyelikTipi === "bireysel" ? 1 : 2);
        if (props.secilenSicilNoSet != undefined)
          props.secilenSicilNoSet(secilenSicilNo);
        props.smsControlledSet(true);
      } else {
        showError(res.data[0].sonucAciklama, 10000);
      }
    } catch (err) {
      showError("Bir hata oluştu. Lütfen tekrar deneyin.");
    } finally {
      setIsLoading(false);
    }
  };

  // ─── ADIM 4: Sicil seçimi yapıldıktan sonra SMS gönder ───
  const handleSicilSecimiDevam = async () => {
    if (secilenSicilNo === null) {
      showError("Lütfen bir sicil kaydı seçiniz.");
      return;
    }
    const recaptchaToken = await recaptchaTokenRef.current?.handleSubmit();
    if (!recaptchaToken) return;

    const cleanPhone = cepTelefonu.replace(/^\+9/, "").replace(/ /g, "");
    setIsLoading(true);
    try {
      const request = new SmsDogrulamaSaveRequest(
        cleanPhone,
        tcKimlikNo || undefined,
        uyelikTipi === "kurumsal" ? vergiNo : undefined,
        secilenSicilNo,
        undefined,
        recaptchaToken,
        undefined,
        true
      );
      const res = await SmsDogrulamaSaveAPI.saveSmsDogrulamaSaveSave(request);
      if (res.data.sonuc === 0) {
        setRefNo(request.referansNo!);
        setStep(3);
        setSeconds(300);
        setIsActive(true);
        setIsError(false);
      } else {
        toast.error(res.data.sonucAciklama);
        recaptchaTokenRef.current?.reset();
      }
    } catch (err) {
      recaptchaTokenRef.current?.reset();
    } finally {
      setIsLoading(false);
    }
  };

  // Tekrar gönder
  const handleTekrarGonder = async () => {
    setDogrulamaKodu("");

    if (dogrulamaYontemi === "sms") {
      const cleanPhone = cepTelefonu
        .replace(/^\+9/, "")
        .replace(/ /g, "");
      const request = new SmsDogrulamaSaveRequest(
        cleanPhone,
        tcKimlikNo || undefined,
        uyelikTipi === "kurumsal" ? vergiNo : undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        true
      );
      try {
        const res =
          await SmsDogrulamaSaveAPI.saveSmsDogrulamaSaveSave(request);
        if (res.data.sonuc === 0) {
          setRefNo(request.referansNo!);
        } else {
          showError(res.data.sonucAciklama);
        }
      } catch (err) {
        // sessiz hata
      }
    } else if (dogrulamaYontemi === "eposta") {
      const request = new EmailRequest(
        ePosta,
        tcKimlikNo || undefined,
        undefined,
        uyelikTipi === "kurumsal" ? vergiNo : undefined,
        false,
        true
      );
      try {
        const res = await EmailAPI.mailGonder(request);
        if (res.sonuc === 0) {
          setRefNo(res.referenceNumber || "");
        } else {
          showError(res.sonucAciklama);
        }
      } catch (err) {
        // sessiz hata
      }
    }
    setSeconds(60);
    setIsActive(true);
  };

  // ─── Seçim Kartı Bileşeni ───
  const SelectionCard = ({
    selected,
    onClick,
    icon,
    title,
    subtitle,
  }: {
    selected: boolean;
    onClick: () => void;
    icon: React.ReactNode;
    title: string;
    subtitle: string;
  }) => (
    <Box
      onClick={onClick}
      sx={{
        border: selected ? "2px solid #1976d2" : "2px solid #e0e0e0",
        borderRadius: 2,
        p: 2,
        cursor: "pointer",
        textAlign: "center",
        flex: 1,
        transition: "all 0.2s",
        backgroundColor: selected ? "#e3f2fd" : "transparent",
        "&:hover": {
          borderColor: "#1976d2",
          backgroundColor: selected ? "#e3f2fd" : "#f5f5f5",
        },
      }}
    >
      <Box sx={{ color: selected ? "#1976d2" : "#757575", mb: 1 }}>
        {icon}
      </Box>
      <Typography variant="subtitle2" fontWeight="bold">
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>
    </Box>
  );

  return (
    <Dialog
      open={true}
      onClose={() => navigate(-1)}
      maxWidth="sm"
      fullWidth
    >
      {/* ─── ADIM 1: Üyelik Türü Seçimi + Kimlik Doğrulama ─── */}
      {step === 1 && (
        <>
          <DialogTitle>
            Üyelik Türü Seçimi
            <IconButton
              aria-label="close"
              onClick={() => navigate(-1)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                pt: 1,
              }}
            >
              <Box sx={{ display: "flex", gap: 2 }}>
                <SelectionCard
                  selected={uyelikTipi === "bireysel"}
                  onClick={() => {
                    setUyelikTipi("bireysel");
                    setVergiNo("");
                    setIsError(false);
                  }}
                  icon={<Person sx={{ fontSize: 40 }} />}
                  title="Bireysel Üyelik"
                  subtitle="TC Kimlik No ile"
                />
                <SelectionCard
                  selected={uyelikTipi === "kurumsal"}
                  onClick={() => {
                    setUyelikTipi("kurumsal");
                    setTcKimlikNo("");
                    setIsError(false);
                  }}
                  icon={<Business sx={{ fontSize: 40 }} />}
                  title="Kurumsal Üyelik"
                  subtitle="Vergi No ile"
                />
              </Box>

              {uyelikTipi === "bireysel" && (
                <TextField
                  size="small"
                  fullWidth
                  label="TC Kimlik Numarası *"
                  variant="outlined"
                  inputProps={{ maxLength: 11 }}
                  value={tcKimlikNo}
                  onChange={(e) =>
                    setTcKimlikNo(e.target.value.replace(/\D/g, ""))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleKimlikDogrulama();
                    }
                  }}
                />
              )}

              {uyelikTipi === "kurumsal" && (
                <TextField
                  size="small"
                  fullWidth
                  label="Vergi Numarası *"
                  variant="outlined"
                  inputProps={{ maxLength: 11 }}
                  value={vergiNo}
                  onChange={(e) =>
                    setVergiNo(e.target.value.replace(/\D/g, ""))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleKimlikDogrulama();
                    }
                  }}
                />
              )}

              <Collapse in={isError}>
                <Alert severity="error">{errorContent}</Alert>
              </Collapse>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={handleKimlikDogrulama}
              color="primary"
              variant="contained"
              disabled={isLoading || !uyelikTipi}
              fullWidth
            >
              {isLoading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                "Devam Et"
              )}
            </Button>
          </DialogActions>
        </>
      )}

      {/* ─── ADIM 2: Doğrulama Yöntemi Seçimi ─── */}
      {step === 2 && (
        <>
          <DialogTitle>
            Doğrulama Yöntemi Seçimi
            <IconButton
              aria-label="close"
              onClick={() => navigate(-1)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                pt: 1,
              }}
            >
              <Box sx={{ display: "flex", gap: 2 }}>
                <SelectionCard
                  selected={dogrulamaYontemi === "sms"}
                  onClick={() => {
                    setDogrulamaYontemi("sms");
                    setIsError(false);
                  }}
                  icon={<PhoneAndroid sx={{ fontSize: 40 }} />}
                  title="SMS ile Doğrulama"
                  subtitle="Cep telefonunuza kod gönderilecek"
                />
                <SelectionCard
                  selected={dogrulamaYontemi === "eposta"}
                  onClick={() => {
                    setDogrulamaYontemi("eposta");
                    setIsError(false);
                  }}
                  icon={<EmailOutlined sx={{ fontSize: 40 }} />}
                  title="E-posta ile Doğrulama"
                  subtitle="E-posta adresinize kod gönderilecek"
                />
              </Box>

              {dogrulamaYontemi === "sms" && (
                <MuiTelInput
                  fullWidth
                  defaultCountry="TR"
                  onlyCountries={["TR"]}
                  size="small"
                  label="Cep Telefonu Numarası *"
                  value={cepTelefonu}
                  inputProps={{ maxLength: 17 }}
                  onChange={(value: any) => {
                    const prefix = "+90";
                    if (!value.startsWith(prefix)) {
                      value = prefix + value.replace(/^\+90/, "");
                    }
                    setCepTelefonu(value);
                  }}
                  variant="outlined"
                />
              )}

              {dogrulamaYontemi === "eposta" && (
                <TextField
                  size="small"
                  fullWidth
                  label="E-posta Adresi *"
                  placeholder="ornek@email.com"
                  variant="outlined"
                  type="email"
                  value={ePosta}
                  onChange={(e) => setEPosta(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleDogrulamaKoduGonder();
                    }
                  }}
                />
              )}

              <Collapse in={isError}>
                <Alert severity="error">{errorContent}</Alert>
              </Collapse>

              <Recaptcha
                ref={recaptchaTokenRef}
                changeHandleRecaptcha={handleRecaptchaChange}
              />
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              flexDirection: "column",
              gap: 1,
              px: 3,
              pb: 2,
            }}
          >
            <Button
              onClick={handleDogrulamaKoduGonder}
              color="primary"
              variant="contained"
              disabled={isLoading || !dogrulamaYontemi}
              fullWidth
            >
              {isLoading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                "Doğrulama Kodunu Gönder"
              )}
            </Button>
            <Button
              onClick={() => {
                setStep(1);
                setIsError(false);
              }}
              color="inherit"
              size="small"
            >
              Geri Dön
            </Button>
          </DialogActions>
        </>
      )}

      {/* ─── ADIM 4: Sicil Kaydı Seçimi ─── */}
      {step === 4 && (
        <>
          <DialogTitle>
            Sicil Kaydı Seçimi
            <IconButton
              aria-label="close"
              onClick={() => navigate(-1)}
              sx={{ position: "absolute", right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
              <Typography>Hangi sicil kaydı ile devam etmek istiyorsunuz?</Typography>
              {sicilListesi.map((sicil: any) => (
                <Box
                  key={sicil.sicilKodu}
                  onClick={() => setSecilenSicilNo(sicil.sicilKodu)}
                  sx={{
                    border: secilenSicilNo === sicil.sicilKodu ? "2px solid #1976d2" : "2px solid #e0e0e0",
                    borderRadius: 2,
                    p: 2,
                    cursor: "pointer",
                    backgroundColor: secilenSicilNo === sicil.sicilKodu ? "#e3f2fd" : "transparent",
                    "&:hover": { borderColor: "#1976d2", backgroundColor: "#f5f5f5" },
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="bold">Sicil No: {sicil.sicilKodu}</Typography>
                  {sicil.unvani && <Typography variant="body2" color="text.secondary">{sicil.unvani}</Typography>}
                </Box>
              ))}
              <Collapse in={isError}>
                <Alert severity="error">{errorContent}</Alert>
              </Collapse>
              <Recaptcha ref={recaptchaTokenRef} changeHandleRecaptcha={handleRecaptchaChange} />
            </Box>
          </DialogContent>
          <DialogActions sx={{ flexDirection: "column", gap: 1, px: 3, pb: 2 }}>
            <Button
              onClick={handleSicilSecimiDevam}
              color="primary"
              variant="contained"
              disabled={isLoading || secilenSicilNo === null}
              fullWidth
            >
              {isLoading ? <CircularProgress size={22} color="inherit" /> : "Devam Et"}
            </Button>
            <Button onClick={() => { setStep(2); setIsError(false); }} color="inherit" size="small">
              Geri Dön
            </Button>
          </DialogActions>
        </>
      )}

      {/* ─── ADIM 3: Doğrulama Kodu Girişi ─── */}
      {step === 3 && (
        <>
          <DialogTitle>
            Doğrulama Kodu Gönderildi
            <IconButton
              aria-label="close"
              onClick={() => navigate(-1)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                pt: 1,
              }}
            >
              {isActive ? (
                <Typography>
                  {dogrulamaYontemi === "sms"
                    ? `Telefonunuza gönderilen 6 haneli doğrulama kodunu giriniz.`
                    : `E-posta adresinize gönderilen 6 haneli doğrulama kodunu giriniz.`}
                </Typography>
              ) : (
                <Typography>
                  Süre doldu. Kodu tekrar göndermeyi deneyin.
                </Typography>
              )}

              {isActive && (
                <TextField
                  size="small"
                  fullWidth
                  label="Doğrulama Kodu"
                  variant="outlined"
                  value={dogrulamaKodu}
                  onChange={(e) =>
                    setDogrulamaKodu(
                      e.target.value
                        .replace(/\D/g, "")
                        .substring(0, 6)
                    )
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleDogrula();
                    }
                  }}
                />
              )}

              <Typography variant="body2" color="text.secondary">
                Kalan süre:{" "}
                {Math.floor(seconds / 60)}:
                {(seconds % 60).toString().padStart(2, "0")}
              </Typography>

              <Collapse in={isError}>
                <Alert severity="error">{errorContent}</Alert>
              </Collapse>
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              justifyContent: "space-between",
              px: 3,
              pb: 2,
            }}
          >
            <Button
              onClick={handleTekrarGonder}
              disabled={isActive}
              size="small"
            >
              Kodu tekrar gönder
            </Button>
            <Button
              onClick={handleDogrula}
              color="primary"
              variant="contained"
              disabled={isLoading || !isActive}
            >
              {isLoading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                "Doğrula"
              )}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default UyelikDogrulamaPopup;
